import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function PlaceUser() {
  const { user, loading: authLoading } = useAuth();
  const [slots, setSlots] = useState([]);
  const [unplacedUsers, setUnplacedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    if (!user || (user.role !== 'admin' && user.role !== 'superadmin')) {
      navigate('/login');
      return;
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, user, navigate]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [slotsRes, usersRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/available-slots`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
        fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/unplaced-users`, {
          headers: { 'Authorization': `Bearer ${token}` },
        }),
      ]);

      if (!slotsRes.ok || !usersRes.ok) {
        throw new Error('Failed to fetch data');
      }

      const slotsData = await slotsRes.json();
      const usersData = await usersRes.json();

      setSlots(slotsData.slots || []);
      setUnplacedUsers(usersData.unplaced_users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePlaceUser = async () => {
    if (!selectedUser || !selectedSlot) {
      setError('Please select both a user and a slot');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const slot = slots[parseInt(selectedSlot)];

      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/mlm/tree/place-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: selectedUser,
          parent_node_id: slot.parent_id,
          leg_position: slot.leg_position,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to place user');
      }

      setSelectedUser('');
      setSelectedSlot('');
      await fetchData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-on-background">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-margin-mobile pt-24">
      <div className="max-w-container-max mx-auto">
        <div className="mb-8">
          <h1 className="text-headline-lg text-on-background mb-2">Place User in Tree</h1>
          <p className="text-body-md text-on-background/60">Manually place approved users in available genealogy slots</p>
        </div>

        {error && (
          <div className="bg-error/10 border border-error text-error p-4 rounded-lg mb-6 text-body-sm">
            {error}
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {/* Unplaced Users */}
          <div className="bg-surface border border-outline rounded-lg p-6">
            <h2 className="text-title-lg text-on-background font-medium mb-4">Unplaced Users</h2>
            {unplacedUsers.length === 0 ? (
              <p className="text-body-md text-on-background/60">No unplaced users</p>
            ) : (
              <div className="space-y-2">
                {unplacedUsers.map((u) => (
                  <label key={u.id} className="flex items-center p-3 bg-background rounded-lg cursor-pointer hover:bg-primary-container/10">
                    <input
                      type="radio"
                      name="user"
                      value={u.id}
                      checked={selectedUser === u.id}
                      onChange={(e) => setSelectedUser(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-on-background">
                        {u.name}
                        {u.pre_processed && (
                          <span className="ml-2 text-body-xs px-2 py-0.5 rounded bg-primary/10 text-primary">Pre-processed</span>
                        )}
                      </div>
                      <div className="text-body-sm text-on-background/60">{u.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Available Slots */}
          <div className="bg-surface border border-outline rounded-lg p-6">
            <h2 className="text-title-lg text-on-background font-medium mb-4">Available Slots</h2>
            {slots.length === 0 ? (
              <p className="text-body-md text-on-background/60">No available slots</p>
            ) : (
              <div className="space-y-2">
                {slots.map((slot, idx) => (
                  <label key={idx} className="flex items-center p-3 bg-background rounded-lg cursor-pointer hover:bg-primary-container/10">
                    <input
                      type="radio"
                      name="slot"
                      value={idx}
                      checked={selectedSlot === idx.toString()}
                      onChange={(e) => setSelectedSlot(e.target.value)}
                      className="mr-3"
                    />
                    <div>
                      <div className="font-medium text-on-background">{slot.description}</div>
                      {slot.parent_affiliate_code && (
                        <div className="text-body-xs text-on-background/60 font-mono">{slot.parent_affiliate_code}</div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={handlePlaceUser}
            disabled={!selectedUser || !selectedSlot || submitting}
            className="px-6 py-3 bg-primary text-on-primary rounded-lg font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {submitting ? 'Placing...' : 'Place User'}
          </button>
          <button
            onClick={() => navigate('/admin')}
            className="px-6 py-3 bg-outline text-on-background rounded-lg font-medium hover:bg-outline/80 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
